/**
 * HRMLESS V3 theme.
 *
 * Colours are the design system's own token values, transcribed from
 * `design/_ds/.../tokens/{colors,typography,effects}.css` (the Claude Design export,
 * since removed; `git checkout 8bf4caa -- design` restores it). They
 * are hex, not oklch: Zudoku passes theme values through verbatim into CSS
 * custom properties (`processColorValue` in zudoku/dist/cli/cli.js only wraps
 * bare "h s l" triples), so hex is emitted as written and stays diffable
 * against the design tokens.
 *
 * The design system is LIGHT ONLY — it publishes no dark palette. Zudoku ships
 * a colour-mode toggle regardless, so the dark set below is derived from the
 * brand rather than invented: ink (#17352b) darkened for the page ground, ink
 * itself for cards, and mint-400 promoted to the action colour so it clears
 * contrast on a dark ground. Marked so it is not mistaken for design output.
 *
 * Webfonts are declared via `theme.fonts` rather than an `@import` in
 * customCss: Zudoku emits font imports at the TOP of the generated theme
 * stylesheet, where CSS requires @import to be. (The previous customCss
 * `@import` for Inter sat after other rules and was being dropped.)
 */

/* ── Design tokens (design/_ds/.../tokens/colors.css) ────────────────────── */
const ink = "#17352b";
const green700 = "#1e5c44";
const green600 = "#2e7d5b";
const mint400 = "#79c1a4";

const surfacePage = "#ffffff";
const surfaceMint = "#eaf3ee";
const surfaceMintSoft = "#f1f7f4";

const textMuted = "#5c6f66";
const borderSoft = "#edf1ef";
const borderSage = "#d5ded9";

const accentCoral = "#ef6b53";

/* Dark ground, derived from ink (no design source — see header comment). */
const inkDeep = "#0f231c";
const inkLine = "#2a4a3e";
const inkPanel = "#1e3f34";
const onDarkMuted = "#a8bdb4";

const theme = {
  fonts: {
    // One Google request covers all three design families. --font-sans is set
    // to Inter (the design's body face); Plus Jakarta Sans and DM Sans are
    // referenced by name from the brand CSS below.
    sans: {
      url: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&family=DM+Sans:wght@400;500;600;700&display=swap",
      fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    },
  },

  light: {
    background: surfacePage,
    foreground: ink,
    card: surfacePage,
    cardForeground: ink,
    popover: surfacePage,
    popoverForeground: ink,
    primary: green700,
    primaryForeground: "#ffffff",
    secondary: surfaceMint,
    secondaryForeground: ink,
    muted: surfaceMintSoft,
    mutedForeground: textMuted,
    accent: surfaceMint,
    accentForeground: ink,
    destructive: accentCoral,
    destructiveForeground: "#ffffff",
    border: borderSage,
    input: borderSage,
    ring: green600,
    // The design's --radius-input (8px). Outer cards are sharp in V3 and the
    // components that own that geometry set it themselves; this value is for
    // Zudoku's own chrome (buttons, code blocks, inputs).
    radius: "0.5rem",
  },

  dark: {
    background: inkDeep,
    foreground: "#ffffff",
    card: ink,
    cardForeground: "#ffffff",
    popover: ink,
    popoverForeground: "#ffffff",
    primary: mint400,
    primaryForeground: inkDeep,
    secondary: inkPanel,
    secondaryForeground: "#ffffff",
    muted: inkPanel,
    mutedForeground: onDarkMuted,
    accent: inkPanel,
    accentForeground: "#ffffff",
    destructive: accentCoral,
    destructiveForeground: inkDeep,
    border: inkLine,
    input: inkLine,
    ring: mint400,
    radius: "0.5rem",
  },

  customCss: `
/* ═══════════════════════════════════════════════════════════════════════
 * HRMLESS brand layer
 *
 * The --hl-* custom properties are the V3 design tokens, exposed globally so
 * SiteHeader / SiteFooter / BlogHome all read the same values. Those three
 * components ship their own scoped CSS (hl- prefixed) and reference these.
 * Update a colour here and the whole shell moves together.
 * ═══════════════════════════════════════════════════════════════════════ */

/* New Astro — the brand wordmark face, used only by components/Logo.tsx.
 * Self-hosted from public/ (nginx serves /pub/*.woff2 with the asset rule).
 * sync_content.py merges into public/ and never clears it, so these survive. */
@font-face {
  font-family: "New Astro";
  src: url("/pub/newastrosemibold.woff2") format("woff2"),
       url("/pub/newastrosemibold.woff") format("woff");
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: "New Astro";
  src: url("/pub/newastroregular.woff2") format("woff2"),
       url("/pub/newastroregular.woff") format("woff");
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

:root {
  /* Ink & greens (brand core) */
  --hl-ink: ${ink};
  --hl-green-700: ${green700};
  --hl-green-600: ${green600};
  --hl-mint-400: ${mint400};
  --hl-mint-300: #a2dbd2;

  /* Surfaces */
  --hl-surface-page: ${surfacePage};
  --hl-surface-mint: ${surfaceMint};
  --hl-surface-mint-soft: ${surfaceMintSoft};
  --hl-surface-mint-pale: #f2f9f5;
  --hl-surface-track: #d9ebe2;
  --hl-surface-chip: #d9f2e5;
  --hl-surface-cream: #f6e7c9;

  /* Accents */
  --hl-accent-coral: ${accentCoral};
  --hl-accent-blue: #3b6ce7;
  --hl-accent-blue-hover: #2f58c4;

  /* Text */
  --hl-text-primary: ${ink};
  --hl-text-soft: rgba(23, 53, 43, 0.85);
  --hl-text-muted: ${textMuted};
  --hl-text-on-dark: #ffffff;
  --hl-text-on-dark-soft: rgba(255, 255, 255, 0.85);

  /* Borders & lines */
  --hl-border-soft: ${borderSoft};
  --hl-border-sage: ${borderSage};

  /* Brand mark (components/Logo.tsx). Light values are the design asset's own
     three teals; dark remaps them to mint so the mark survives the ink ground. */
  --hl-logo-deep: #1d4547;
  --hl-logo-mid: #235456;
  --hl-logo-light: #a2dbd2;
  --hl-logo-word: #1d4547;

  /* Type (tokens/typography.css) */
  --hl-font-display: "Plus Jakarta Sans", Helvetica, Arial, sans-serif;
  --hl-font-body: "Inter", Helvetica, Arial, sans-serif;
  --hl-font-ui: "DM Sans", "Inter", Helvetica, Arial, sans-serif;

  /* Effects (tokens/effects.css) — ink-tinted, never gray */
  --hl-shadow-card: 0 4px 14px rgba(23, 53, 43, 0.06);
  --hl-shadow-menu: 0 8px 24px rgba(23, 53, 43, 0.10);
  --hl-shadow-float: 0 20px 40px -12px rgba(23, 53, 43, 0.28);
  --hl-ease-out-expo: cubic-bezier(0.16, 1, 0.3, 1);
  --hl-duration-fast: 0.3s;
}

/* Dark mode. No design source — the same derivation as theme.dark above, so
 * the shell components and the Zudoku docs body agree instead of seaming. */
.dark, [data-theme="dark"] {
  --hl-surface-page: ${inkDeep};
  --hl-surface-mint: ${ink};
  --hl-surface-mint-soft: ${inkPanel};
  --hl-surface-mint-pale: ${inkPanel};
  --hl-surface-track: ${inkLine};
  --hl-surface-chip: ${inkPanel};
  --hl-surface-cream: ${inkPanel};

  --hl-green-700: ${mint400};
  --hl-green-600: #a2dbd2;

  --hl-text-primary: #ffffff;
  --hl-text-soft: rgba(255, 255, 255, 0.85);
  --hl-text-muted: ${onDarkMuted};

  --hl-border-soft: ${inkLine};
  --hl-border-sage: ${inkLine};

  --hl-logo-deep: #a2dbd2;
  --hl-logo-mid: ${mint400};
  --hl-logo-light: ${surfaceMint};
  --hl-logo-word: #ffffff;

  --hl-shadow-card: 0 4px 14px rgba(0, 0, 0, 0.35);
  --hl-shadow-menu: 0 8px 24px rgba(0, 0, 0, 0.45);
  --hl-shadow-float: 0 20px 40px -12px rgba(0, 0, 0, 0.55);
}

body {
  font-family: var(--hl-font-body);
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

/* Headings use the display face, per tokens/typography.css. */
h1, h2, h3, h4 {
  font-family: var(--hl-font-display);
  letter-spacing: -0.5px;
}

/* ── Pill controls (shared with the footer CTA) ────────────────── */
.hl-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 24px;
  border-radius: 999px;
  font-family: var(--hl-font-ui);
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: color var(--hl-duration-fast) ease,
              background-color var(--hl-duration-fast) ease,
              border-color var(--hl-duration-fast) ease;
}
.hl-btn--outline {
  border: 1px solid var(--hl-text-primary);
  color: var(--hl-text-primary);
  background: transparent;
}
.hl-btn--outline:hover {
  color: var(--hl-green-600);
  border-color: var(--hl-green-600);
}
.hl-btn--blue {
  background: var(--hl-accent-blue);
  color: #ffffff;
  border: 1px solid var(--hl-accent-blue);
}
.hl-btn--blue:hover {
  background: var(--hl-accent-blue-hover);
  border-color: var(--hl-accent-blue-hover);
}
/* Ghost pill on the ink CTA band — the marketing footer's "Explore platform". */
.hl-btn--ghost-dark {
  border: 1px solid var(--hl-text-on-dark);
  color: var(--hl-text-on-dark);
  background: transparent;
}
.hl-btn--ghost-dark:hover {
  color: var(--hl-mint-300);
  border-color: var(--hl-mint-300);
}
.hl-btn--lg { height: 52px; padding: 0 32px; font-size: 17px; }
.hl-btn--block { width: 100%; }

/* ── Zudoku chrome ─────────────────────────────────────────────────────── */

/* Hide default Zudoku header and footer — replaced by custom slot components */
header[data-pagefind-ignore] {
  display: none !important;
}
/* Reset default Zudoku footer — custom footer injected via footer-after slot */
footer {
  border-top: none !important;
  background: transparent !important;
}
footer > div {
  padding: 0 !important;
  max-width: none !important;
}
/* Hide the default footer's own bars (logo, copyright, social from site.footer)
 * while keeping the wrapper that carries the footer-after slot. Anchored on
 * "does not contain our footer" rather than on child position: with an empty
 * site.footer, Zudoku currently renders the slot row first and an empty bottom
 * bar last, but a :last-child rule would blank SiteFooter itself the moment
 * that empty bar stops being emitted. */
footer > div > div:not(:has(.hl-footer)) {
  display: none !important;
}

/* Hide "powered by Zudoku" badge and sidebar collapse button */
a[href="https://zudoku.dev"] {
  display: none !important;
}
/* Hide sidebar bottom section (collapse toggle + powered-by) */
div:has(> a[href="https://zudoku.dev"]) {
  display: none !important;
}

/* The custom header is sticky, so anchored headings would land underneath it. */
:target {
  scroll-margin-top: 6rem;
}

/* Post byline (components/PostByline.tsx, content-before slot) — the author
 * above the article body. Reads the --hl-* tokens above so it moves with the
 * rest of the shell. */
.post-byline {
  margin: 0 0 2rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--hl-border-soft, #edf1ef);
}
.post-byline__link {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: inherit;
}
.post-byline__avatar {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  object-fit: cover;
  background: var(--hl-surface-mint-soft, #f1f7f4);
  flex: none;
}
.post-byline__text {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.post-byline__name {
  font-weight: 600;
  color: var(--hl-ink, #17352b);
}
.post-byline__link:hover .post-byline__name {
  color: var(--hl-green-600, #2e7d5b);
}
.post-byline__meta {
  font-size: 0.8125rem;
  color: var(--hl-text-muted, #5c6f66);
}

/* Accessibility floor (WCAG 2.3.3 / 2.2.2) — carried over from the public
 * site's index.css so the two behave the same for reduced-motion users. */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
  `,
} as const;

export default theme;
